import { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Form, message } from 'antd'
import dayjs from 'dayjs'
import { useCrashDetectionDetail } from '@/services/crash-detection/useCrashDetectionDetail'
import { useUpdateCrashDetection } from '@/services/crash-detection/useUpdateCrashDetection'
import {
  useAssignCrashDetectionOwner,
  AssignCrashDetectionOwnerErrorCode,
  AssignCrashDetectionOwnerErrorData,
} from '@/services/crash-detection/useAssignCrashDetectionOwner'
import {
  CrashContactStatus,
  CrashIncidentStatus,
  CrashUserRespondType,
} from '@/services/crash-detection/types'
import CONSTANT from '@/constant'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import { validatePerm } from '@/libs/validatePermission'
import userAtom from '@/recoil/user'
import crashDetectionLockedModalAtom from '@/recoil/crashDetectionLockedModal'

const THAI_MONTHS_SHORT = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
]

const formatThaiBE = (dateStr: string | Date | undefined | null) => {
  if (!dateStr) return '-'
  const d = dayjs(dateStr)
  if (!d.isValid()) return '-'
  return `${d.date()} ${THAI_MONTHS_SHORT[d.month()]} ${d.year() + 543}, ${d.format('HH:mm')}`
}

type StatusBadge = { label: string; bg: string; color: string }

const STATUS_BADGE: Record<'sentNoti' | 'crash' | 'noCrash' | 'noResponse', StatusBadge> = {
  sentNoti: { label: 'ส่ง noti แจ้งการชน', bg: '#fff8e1', color: '#ffa202' },
  crash: { label: 'กดยืนยันการชน', bg: '#fde7e5', color: '#ef2505' },
  noCrash: { label: 'กดไม่ต้องการความช่วยเหลือ', bg: '#eaebef', color: '#666666' },
  noResponse: { label: 'ไม่มีการกด', bg: '#eaebef', color: '#666666' },
}

const deriveStatusBadge = (
  userRespondType: CrashUserRespondType | null | undefined,
): StatusBadge => {
  if (userRespondType === CrashUserRespondType.CRASH) return STATUS_BADGE.crash
  if (userRespondType === CrashUserRespondType.NO_CRASH) return STATUS_BADGE.noCrash
  if (userRespondType === CrashUserRespondType.NO_ACTION) return STATUS_BADGE.noResponse
  return STATUS_BADGE.sentNoti
}

type FormValues = {
  currentContactStatus: CrashContactStatus
  currentIncidentStatus: CrashIncidentStatus
  isSendSurveyor: boolean
  notifyNumber: string
  note: string
}

const useCrashDetectionDetailPageHook = (mode?: 'view' | 'edit') => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useRecoilValue(userAtom)
  const { data, isLoading } = useCrashDetectionDetail(id!)
  const { mutate: updateCase, isPending: isUpdating } = useUpdateCrashDetection()
  const { mutate: assignOwner } = useAssignCrashDetectionOwner()
  const setLockedModal = useSetRecoilState(crashDetectionLockedModalAtom)
  const [form] = Form.useForm()

  // Stores the normalized initial form values so the dirty check compares like-for-like
  // and so the form isn't reset by background refetches mid-edit.
  const initialValuesRef = useRef<FormValues | null>(null)

  const canEdit = validatePerm([BACKOFFICE_USER_PERMISSION.CRS010102], user.permissions)
  const isViewMode = mode === 'view'

  useEffect(() => {
    if (!data) return

    const isCrashCase = data.userRespondType === CrashUserRespondType.CRASH

    // Redirect to view if case is not editable or user lacks edit permission
    if (mode === 'edit' && !canEdit) {
      message.warning('เคสนี้ไม่สามารถแก้ไขได้')
      navigate(
        CONSTANT.ROUTES.CRASH_DETECTION_VIEW_PAGE.replace(':id', data.crashDetectionCrashInfoId!),
      )
      return
    }

    // Only initialize once per mount — avoid clobbering in-progress edits on refetch
    if (initialValuesRef.current) return

    // Normalize contactStatus: non-crash cases always show NOT_APPLICABLE;
    // crash initial state defaults to NOT_CONTACTED if backend returns null.
    const normalizedContactStatus = (() => {
      if (data.currentContactStatus) return data.currentContactStatus
      return isCrashCase ? CrashContactStatus.NOT_CONTACTED : CrashContactStatus.NOT_APPLICABLE
    })()

    // Normalize incidentStatus: null and 'noStatus' both mean "ยังไม่ทราบสถานะ"
    const normalizedIncidentStatus = data.currentIncidentStatus || CrashIncidentStatus.NO_STATUS

    const initial: FormValues = {
      currentContactStatus: normalizedContactStatus,
      currentIncidentStatus: normalizedIncidentStatus,
      isSendSurveyor: data.isSendSurveyor || false,
      notifyNumber: data.notifyNumber || '',
      note: data.note || '',
    }

    initialValuesRef.current = initial
    form.setFieldsValue(initial)
  }, [data, mode, navigate, form, canEdit])

  const handleContactStatusChange = (value: CrashContactStatus) => {
    if (value !== CrashContactStatus.CONTACTED) {
      form.setFieldsValue({
        currentIncidentStatus: CrashIncidentStatus.NO_STATUS,
        isSendSurveyor: false,
      })
    }
    // Clear stale note validation error — required state may have changed
    form.setFields([{ name: 'note', errors: [] }])
  }

  const handleIncidentStatusChange = (value: CrashIncidentStatus) => {
    if (value !== CrashIncidentStatus.CRASH) {
      form.setFieldsValue({ isSendSurveyor: false, notifyNumber: '' })
    }
    // Clear stale note validation error — required state may have changed
    form.setFields([{ name: 'note', errors: [] }])
  }

  const handleSave = async () => {
    if (!id) return
    try {
      const values = await form.validateFields()
      updateCase(
        { id, data: values },
        {
          onSuccess: () => {
            message.success('บันทึกข้อมูลเรียบร้อยแล้ว')
            navigate(CONSTANT.ROUTES.CRASH_DETECTION_PAGE)
          },
          onError: () => {
            message.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
          },
        },
      )
    } catch {
      // form.validateFields() will highlight errors automatically
    }
  }

  const handleNavigateBack = () => {
    navigate(CONSTANT.ROUTES.CRASH_DETECTION_PAGE)
  }

  // "Cancel" semantically distinct from "back" but same target route
  const handleCancel = handleNavigateBack

  const handleNavigateToEdit = () => {
    if (!data?.crashDetectionCrashInfoId) return
    const crashDetectionCrashInfoId = data.crashDetectionCrashInfoId
    // Claim the case before entering edit mode. 204 (incl. idempotent re-claim) → navigate;
    // BIZBOCRASH1001 → another owner holds the 2-hour lock, show the locked modal instead.
    assignOwner(crashDetectionCrashInfoId, {
      onSuccess: () => {
        navigate(
          CONSTANT.ROUTES.CRASH_DETECTION_EDIT_PAGE.replace(':id', crashDetectionCrashInfoId),
        )
      },
      onError: (error: any) => {
        const errorBody = error?.response?.data
        const code = errorBody?.code
        if (code === AssignCrashDetectionOwnerErrorCode.LOCKED_BY_OTHER_OWNER) {
          // Owner identity comes from the error body's `data`, not the detail response.
          const owner = errorBody?.data as AssignCrashDetectionOwnerErrorData | undefined
          setLockedModal({
            isVisible: true,
            errorCode: code,
            ownerName: owner?.ownerName,
            ownerEmployeeCode: owner?.ownerEmployeeCode,
          })
          return
        }
        if (code === AssignCrashDetectionOwnerErrorCode.LOCKED_BY_CURRENT_USER_OTHER_CASE) {
          const owner = errorBody?.data as AssignCrashDetectionOwnerErrorData | undefined
          setLockedModal({
            isVisible: true,
            errorCode: code,
            lockedCrashInfoId: owner?.crashDetectionCrashInfoId,
          })
          return
        }
        if (code === AssignCrashDetectionOwnerErrorCode.CASE_NOT_FOUND) {
          message.error('ไม่พบรายการตรวจจับการชนนี้')
          return
        }
        message.error('เกิดข้อผิดพลาดในการเข้าถึงรายการ')
      },
    })
  }

  const handleCopyCoordinates = () => {
    if (data?.latitude == null || data?.longitude == null) return
    navigator.clipboard.writeText(`${data.latitude},${data.longitude}`)
    message.success('คัดลอกพิกัดเรียบร้อยแล้ว')
  }

  const isFormDirty = (currentValues: Record<string, unknown>) => {
    const init = initialValuesRef.current
    if (!init) return false
    return (Object.keys(init) as (keyof FormValues)[]).some((k) => currentValues[k] !== init[k])
  }

  const statusBadge = deriveStatusBadge(data?.userRespondType)
  const hasCoordinates = typeof data?.latitude === 'number' && typeof data?.longitude === 'number'
  const formattedIncidentDate = formatThaiBE(data?.incidentDateTime)
  const isCrashCase = data?.userRespondType === CrashUserRespondType.CRASH
  const showEditButton =
    isViewMode &&
    canEdit &&
    !!data?.crashDetectionCrashInfoId

  return {
    form,
    data,
    isLoading,
    isUpdating,
    canEdit,
    isViewMode,
    statusBadge,
    hasCoordinates,
    formattedIncidentDate,
    isCrashCase,
    showEditButton,
    handleContactStatusChange,
    handleIncidentStatusChange,
    handleSave,
    handleCancel,
    handleNavigateBack,
    handleNavigateToEdit,
    handleCopyCoordinates,
    isFormDirty,
  }
}

export default useCrashDetectionDetailPageHook
